import type { Routes, CanActivateFn } from '@angular/router';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { MsalGuard } from '@azure/msal-angular';
import { featureGuard } from './shared/feature.guard';

const SESSION_PORTFOLIO_KEY = 'dashboard.lastPortfolioGroupId';

const dashboardRedirectGuard: CanActivateFn = () => {
  const router = inject(Router);
  try {
    const savedId = sessionStorage.getItem(SESSION_PORTFOLIO_KEY);
    if (savedId) {
      return router.createUrlTree(['/dashboard', savedId]);
    }
  } catch { /* ignore */ }
  return true;
};

export const routes: Routes = [
	{
		path: 'login',
		loadComponent: () => import('./login/login').then(m => m.LoginComponent)
	},
	// Dashboard without portfolioId — redirect to last used portfolio if one is saved
	{
		path: 'dashboard',
		loadComponent: () => import('./dashboard/dashboard').then(m => m.DashboardComponent),
		canActivate: [MsalGuard, dashboardRedirectGuard],
		// // children: [
		// // 	{
		// // 		path: 'properties',
		// // 		loadComponent: () => import('./properties-list/properties-list').then(m => m.PropertiesListComponent)
		// // 	}
		// // ]
	},
	// Dashboard with portfolioId param (optional) so URLs can be /dashboard/:portfolioId/... 
	{
		path: 'dashboard/:portfolioId',
		loadComponent: () => import('./dashboard/dashboard').then(m => m.DashboardComponent),
		canActivate: [MsalGuard],
		children: [
			{
				path: '',
				redirectTo:'properties',
				pathMatch: 'full'
			},
			{
				path: 'properties',
				loadComponent: () => import('./properties-list/properties-list').then(m => m.PropertiesListComponent)
			},
			{
				path: 'add-property',
				loadComponent: () => import('./add-property/add-property').then(m => m.AddPropertyComponent),
				canActivate: [featureGuard('addProperty')]
			},
			{
				path: 'edit-property/:propertyId',
				loadComponent: () => import('./edit-property/edit-property').then(m => m.EditPropertyComponent)
			},
			{
				path: 'match-review',
				loadComponent: () => import('./match-review/match-review').then(m => m.MatchReviewComponent)
			}
		]
	},
	// Dashboard without portfolioId (optional param scenario)
	{
		path: 'reports',
		loadComponent: () => import('./dashboard/dashboard').then(m => m.DashboardComponent),
		canActivate: [MsalGuard],
		// children: [
		// 	{
		// 		path: 'profisee-master-report',
		// 		loadComponent: () => import('./ReportComponents/ProfiseeMasterReportComponent/profisee-master-report').then(m => m.ProfiseeMasterReportComponent)
		// 	}
		// ]
	},
	// {
	// 	path: 'reports/:portfolioId',
	// 	loadComponent: () => import('./dashboard/dashboard').then(m => m.DashboardComponent),
	// 	canActivate: [MsalGuard],
	// 	children: [
	// 		{
	// 			path: '',
	// 			redirectTo: 'profisee-master-report',
	// 			pathMatch: 'full'
	// 		},
	// 		{
	// 			path: 'profisee-master-report',
	// 			loadComponent: () => import('./ReportComponents/ProfiseeMasterReportComponent/profisee-master-report').then(m => m.ProfiseeMasterReportComponent)
	// 		}
	// 	]
	// },
	{
		path: 'clientconfig',
		loadComponent: () => import('./client/client').then(m => m.ClientComponent),
		canActivate: [MsalGuard]
	},
	{
		path: 'servicelineconfig',
		loadComponent: () => import('./servicelineconfig/servicelineconfig').then(m => m.ServiceLineConfigComponent),
		canActivate: [MsalGuard]
	},
	{
		path: 'create-portfolio',
		loadComponent: () => import('./create-portfolio/create-portfolio').then(m => m.CreatePortfolioComponent),
		canActivate: [MsalGuard, featureGuard('createPortfolio')]
	},
	{
		path: 'batch-history',
		loadComponent: () => import('./batch-history/batch-history').then(m => m.BatchHistoryComponent),
		canActivate: [MsalGuard]
	},
	{
		path: 'property-change-log',
		loadComponent: () => import('./property-change-log/property-change-log').then(m => m.PropertyChangeLogComponent),
		canActivate: [MsalGuard]
	},
	{
		path: 'property-csv-uploads',
		loadComponent: () => import('./property-csv-uploads/property-csv-uploads').then(m => m.PropertyCsvUploadsComponent),
		canActivate: [MsalGuard, featureGuard('bulkUpload')]
	},
	{
		path: 'source-property-auto-match-manager',
		loadComponent: () => import('./source-property-auto-match-manager/source-property-auto-match-manager').then(m => m.SourcePropertyAutoMatchManagerComponent),
		canActivate: [MsalGuard]
	},
	// {
	// 	path: 'suspected-matches',
	// 	loadComponent: () => import('./merge-matches/merge-matches').then(m => m.MergeMatchesComponent),
	// 	canActivate: [MsalGuard]
	// },
	{
		path: 'user-roles',
		loadComponent: () => import('./user-roles/user-roles.component').then(m => m.UserRolesComponent),
		canActivate: [MsalGuard]
	},
	{
		path: 'user-access-log',
		loadComponent: () => import('./user-access-log/user-access-log').then(m => m.UserAccessLogComponent),
		canActivate: [MsalGuard]
	},
	{
		path: 'triana-property-audit',
		loadComponent: () => import('./ReportComponents/TrianaPropertyAuditComponent/triana-property-audit').then(m => m.TrianaPropertyAuditComponent),
		canActivate: [MsalGuard]
	},
	{
		path: 'no-address-properties',
		loadComponent: () => import('./no-address-properties/no-address-properties').then(m => m.NoAddressPropertiesComponent),
		canActivate: [MsalGuard]
	},
	{
		path: 'unmastered-properties',
		loadComponent: () => import('./unmastered-properties/unmastered-properties').then(m => m.UnmasteredPropertiesComponent),
		canActivate: [MsalGuard]
	},
	{
		// removed root-level add-property; now a child of dashboard/:portfolioId
		path: 'add-property',
		redirectTo: 'dashboard' // in case someone hits old URL without portfolio context
	},
	// {
	// 	path: 'add-mdm-property',
	// 	loadComponent: () => import('./addmdmproperty/addmdmproperty').then(m => m.AddMdmPropertyComponent),
	// 	canActivate: [MsalGuard]
	// },
	{ path: '', pathMatch: 'full', redirectTo: 'dashboard' },
	{ path: '**', redirectTo: 'dashboard' }
];
